"use client";

import { useEffect, useRef } from "react";
import { MoreVertical } from "lucide-react";

interface EllipsisMenuProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onDelete: () => void;
  onCopyLink?: () => void; // Optional copy link action
}

/**
 * Three-dot menu component for post/reply actions
 * Shows dropdown menu when open, handles click-outside-to-close
 */
export function EllipsisMenu({
  isOpen,
  onOpen,
  onClose,
  onDelete,
  onCopyLink,
}: EllipsisMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add listener with a small delay to avoid immediate close on open click
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle Escape key to close menu
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={isOpen ? onClose : onOpen}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-[#262636] bg-[#0a0a12] transition hover:border-[rgba(255,255,255,0.16)] hover:bg-[#141422]"
        aria-label="More options"
        aria-expanded={isOpen}
      >
        <MoreVertical className="h-4 w-4 text-[#a3a3c2]" />
      </button>

      {isOpen && (
        <div className="absolute right-0.5 top-10 z-50 w-fit rounded-[12px] border border-[#262636] bg-[#0a0a12] shadow-[0_8px_24px_rgba(0,0,0,0.5)]">
          {onCopyLink && (
            <button
              onClick={() => {
                onCopyLink();
                onClose();
              }}
              className="w-full rounded-t-[12px] px-4 py-2.5 text-left text-sm text-white transition hover:bg-[#141422]"
            >
              Copy link
            </button>
          )}
          <button
            onClick={() => {
              onDelete();
              onClose();
            }}
            className={`w-full px-4 py-2.5 text-left text-sm text-white transition hover:bg-[#141422] hover:text-red-400 ${
              onCopyLink ? "" : "rounded-t-[12px]"
            } ${onCopyLink ? "rounded-b-[12px]" : ""}`}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

